/**
 * Account session and server-verified entitlement.
 *
 * Sign-in is passwordless: the person receives a one-time link, and the app
 * exchanges it for a session token it keeps in localStorage. Entitlement is
 * read from the server rather than stored locally, so a subscription follows
 * the person between devices and cannot be granted by editing storage.
 */

import axios from 'axios';

const AUTH_URL =
  process.env.REACT_APP_AUTH_URL ||
  'https://snnzcfq3kr7q2qi2ikoo7n3un40jlczv.lambda-url.us-east-1.on.aws/';

const SESSION_KEY = 'astrobot.session';
const TIMEOUT = 15000;

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (e) {
    // Storage unavailable: the session lasts for this page only.
  }
}

export function getSession() {
  return safeGet(SESSION_KEY);
}

export function isSignedIn() {
  return Boolean(getSession());
}

export function signOut() {
  safeSet(SESSION_KEY, null);
}

/** Email a one-time sign-in link. Resolves even for an unknown address. */
export async function requestSignInLink(email) {
  const { data } = await axios.post(
    `${AUTH_URL}auth/request`,
    { email },
    { timeout: TIMEOUT }
  );
  return data;
}

/**
 * Exchange a link token for a session.
 * @returns {{email, subscribed, charts}}
 */
export async function verifyToken(token) {
  const { data } = await axios.post(
    `${AUTH_URL}auth/verify`,
    { token },
    { timeout: TIMEOUT }
  );
  if (data.session) safeSet(SESSION_KEY, data.session);
  return data;
}

/**
 * The signed-in account and its entitlement.
 * @returns {{email, subscribed, charts}|null} null when not signed in.
 */
export async function fetchAccount() {
  const session = getSession();
  if (!session) return null;
  try {
    const { data } = await axios.post(
      `${AUTH_URL}auth/me`,
      { session },
      { timeout: TIMEOUT }
    );
    return data;
  } catch (e) {
    // A rejected session is stale or forged; drop it rather than looping.
    if (e.response && e.response.status === 401) signOut();
    return null;
  }
}

/**
 * Sign in whoever just paid, using the subscription Dodo returned.
 *
 * The server confirms the id with Dodo and reads the email from Dodo's own
 * record, so the person never types the address they used at checkout and
 * cannot mistype it.
 *
 * @returns {{email, subscribed, charts}|null} null when it could not be confirmed
 */
export async function claimSubscription(subscriptionId) {
  try {
    const { data } = await axios.post(
      `${AUTH_URL}auth/claim`,
      { subscription_id: subscriptionId },
      { timeout: TIMEOUT }
    );
    if (data.session) safeSet(SESSION_KEY, data.session);
    return data;
  } catch (e) {
    return null;
  }
}

/** Save this account's charts so they follow the person between devices. */
export async function syncCharts(charts) {
  const session = getSession();
  if (!session) return false;
  try {
    await axios.post(
      `${AUTH_URL}auth/sync`,
      { session, charts },
      { timeout: TIMEOUT }
    );
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Send feedback to the team.
 *
 * Works signed out on purpose: someone whose chart looks wrong is often
 * exactly the person who has not made an account.
 */
export async function sendFeedback({ message, email, profile }) {
  const { data } = await axios.post(
    `${AUTH_URL}feedback`,
    {
      message,
      email,
      profile,
      session: getSession(),
      page: typeof window !== 'undefined' ? window.location.href : '',
    },
    { timeout: TIMEOUT }
  );
  return data;
}

/**
 * Read and strip a ?token= from the URL, so a refresh cannot replay it.
 * @returns {string|null} the token, if this load carried one
 */
export function consumeUrlToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return null;
    params.delete('token');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '')
    );
    return token;
  } catch (e) {
    return null;
  }
}
